import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HashLink } from 'react-router-hash-link';
import {
  LuArrowRight,
  LuClock,
  LuHouse,
  LuMail,
  LuMapPin,
  LuPhone,
  LuSparkles,
} from 'react-icons/lu';
import { publicApi } from '../../services/publicApi';

const labelClass = 'mb-2 block text-sm font-semibold text-black dark:text-white';
const bodyClass = 'text-sm font-normal leading-relaxed text-black/55 dark:text-white/55';
const inputClass =
  'w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm font-normal text-black outline-none transition-all placeholder:text-black/35 focus:border-primary focus:ring-2 focus:ring-primary/25 dark:border-white/15 dark:bg-[#161616] dark:text-white dark:placeholder:text-white/35';

function ContactInfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 border-b border-black/10 py-5 last:border-b-0 dark:border-white/10">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-black">
        <Icon size={20} strokeWidth={2} />
      </div>
      <div>
        <p className="text-sm font-semibold text-black dark:text-white">{label}</p>
        <p className={`mt-0.5 ${bodyClass}`}>{value}</p>
      </div>
    </div>
  );
}

function FormField({ id, label, type = 'text', as = 'input', className = '', ...props }) {
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {as === 'textarea' ? (
        <textarea id={id} className={`${inputClass} min-h-[132px] resize-y`} {...props} />
      ) : (
        <input id={id} type={type} className={inputClass} {...props} />
      )}
    </div>
  );
}

const cardClass =
  'h-full rounded-[1.75rem] border border-black/10 bg-white p-8 shadow-[0_18px_45px_rgba(0,0,0,0.06)] lg:p-9 dark:border-white/10 dark:bg-[#101010] dark:shadow-[0_18px_45px_rgba(0,0,0,0.35)]';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    content: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    address: '',
  });

  useEffect(() => {
    let isMounted = true;
    const loadContactInfo = async () => {
      try {
        const data = await publicApi.getContactInformation();
        const selected = Array.isArray(data) ? data[0] : null;
        if (!isMounted) return;
        if (!selected) {
          setContactInfo({ email: '', phone: '', address: '' });
          return;
        }

        setContactInfo({
          email: selected.email || '',
          phone: selected.phone || '',
          address: selected.address || '',
        });
      } catch {
        if (isMounted) setContactInfo({ email: '', phone: '', address: '' });
      }
    };

    loadContactInfo();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      await publicApi.sendMessage({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        content: formData.content,
        isRead: false,
      });

      setStatus({ type: 'success', message: 'Message sent successfully. We will contact you soon.' });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        content: '',
        phone: '',
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to send message.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="overflow-x-hidden bg-white font-sans transition-colors duration-300 dark:bg-black">
      <div className="px-6 pb-28 pt-16 md:px-10 md:pt-20 lg:px-16">
        <div className="mx-auto max-w-7xl text-center">
          <h2 className="text-4xl font-bold tracking-tight text-black dark:text-white md:text-[2.75rem]">
            Contact Us
          </h2>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-5 py-2.5 text-sm font-normal text-black dark:border-primary/40 dark:bg-primary/15 dark:text-white">
            <LuHouse size={15} className="text-primary" />
            <HashLink smooth to="/#home" className="font-normal transition-colors hover:text-primary">
              Home
            </HashLink>
            <span className="text-primary">/</span>
            <span className="font-semibold text-black dark:text-white">Contact Us</span>
          </div>
        </div>
      </div>

      <div className="relative mx-auto -mt-20 max-w-7xl px-6 pb-20 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="min-w-0 lg:sticky lg:top-24"
          >
            <div className={cardClass}>
              <div className="mb-4 flex items-center gap-3">
                <span className="h-8 w-1.5 shrink-0 rounded-full bg-primary" />
                <h3 className="text-xl font-bold tracking-tight text-black dark:text-white">
                  Contact Information
                </h3>
              </div>
              <p className={`mb-2 max-w-sm ${bodyClass}`}>
                We&apos;re ready to help you with your next project. Reach out through any of the channels below and
                our team will respond as soon as possible.
              </p>

              <div className="mt-2">
                <ContactInfoRow icon={LuPhone} label="Phone Number" value={contactInfo.phone} />
                <ContactInfoRow icon={LuMail} label="Email Address" value={contactInfo.email} />
                <ContactInfoRow icon={LuClock} label="Opening Hour" value="Mon - Fri: 9:00 AM - 6:00 PM" />
                <ContactInfoRow icon={LuMapPin} label="Our Location" value={contactInfo.address} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="min-w-0"
          >
            <div className={`${cardClass} border-primary/25`}>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-2 text-xs font-semibold text-black dark:bg-primary/20 dark:text-white">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-black">
                  <LuSparkles size={13} />
                </span>
                Get In Touch
              </span>

              <h3 className="mt-5 text-2xl font-bold tracking-tight text-black dark:text-white md:text-3xl">
                Get In <span className="text-primary">Touch</span>
              </h3>
              <div className="mt-2 h-1 w-16 rounded-full bg-primary" />
              <p className={`mt-3 max-w-xl ${bodyClass}`}>
                Have a project in mind or need expert guidance? Fill out the form and our team will get back to you
                shortly.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    id="firstName"
                    label="First Name"
                    placeholder="Ali"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    required
                  />
                  <FormField
                    id="lastName"
                    label="Last Name"
                    placeholder="Muuse"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    id="email"
                    label="Email Address"
                    type="email"
                    placeholder="info@raadsotech.com"
                    value={formData.email}
                    onChange={handleChange('email')}
                    required
                  />
                  <FormField
                    id="phone"
                    label="Phone Number"
                    placeholder="+252..."
                    value={formData.phone}
                    onChange={handleChange('phone')}
                  />
                </div>

                <FormField
                  id="subject"
                  label="Subject"
                  placeholder="Project Inquiry"
                  value={formData.subject}
                  onChange={handleChange('subject')}
                  required
                />

                <FormField
                  id="message"
                  label="Message"
                  as="textarea"
                  placeholder="Tell us about your project..."
                  value={formData.content}
                  onChange={handleChange('content')}
                  required
                />

                {status.message ? (
                  <p
                    className={`text-sm font-normal ${
                      status.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {status.message}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full max-w-xs items-center justify-between rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-black shadow-[0_10px_28px_rgba(243,199,21,0.35)] transition-transform hover:scale-[1.01] disabled:opacity-70"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-primary">
                    <LuArrowRight size={16} strokeWidth={2.5} />
                  </span>
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
